import React, { useRef } from 'react';
import { JcButton, JcButtonType } from './button/JcButton';
import { JcCommonProps } from '../utils/JcUtils';
import { useJcFieldId } from '../utils/useJcFieldId';

export interface JcFileInputProps extends JcCommonProps {
  /** Called with the chosen file. Re-selecting the same file fires again. */
  readonly onFileSelected: (file: File) => void;
  /** Native `accept` filter, e.g. `"image/*"`. */
  readonly accept?: string;
  readonly disabled?: boolean;
  readonly buttonType?: JcButtonType;
  readonly children: React.ReactNode;
}

/**
 * A button that opens the native file picker, hiding the `<input type="file">`
 * behind it. Yields the chosen `File`; the caller owns upload and progress.
 */
export const JcFileInput: React.FunctionComponent<JcFileInputProps> = (props: JcFileInputProps) => {
  // The id belongs on the file input itself, so keep it off the visible button.
  const { onFileSelected, accept, disabled, buttonType, children, id, ...commonProps } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useJcFieldId(id);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      onFileSelected(file);
    }
    // Clear so selecting the same file again still fires onChange.
    event.target.value = '';
  };

  return (
    <>
      <JcButton
        {...commonProps}
        buttonType={buttonType}
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        {children}
      </JcButton>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        disabled={disabled}
        style={{ display: 'none' }}
        onChange={handleChange}
      />
    </>
  );
};
